"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { routing, type AppLocale } from "@/i18n/routing";

type LanguageSwitcherProps = {
  currentLocale: AppLocale;
};

// Render locale links in the top-right corner with no client-side state.
export function LanguageSwitcher({ currentLocale }: LanguageSwitcherProps) {
  const pathname = usePathname();

  // Build locale-preserving href by swapping only the first locale segment.
  function getLocalizedHref(targetLocale: AppLocale): string {
    const normalizedPath = pathname && pathname.startsWith("/") ? pathname : `/${currentLocale}`;
    const segments = normalizedPath.split("/").filter(Boolean);

    if (segments.length > 0 && routing.locales.includes(segments[0] as AppLocale)) {
      segments[0] = targetLocale;
      return `/${segments.join("/")}`;
    }

    return `/${targetLocale}`;
  }

  return (
    <nav aria-label="Selecao de idioma" className="flex items-center gap-2 text-sm font-medium">
      {routing.locales.map((locale) => {
        const isActive = locale === currentLocale;

        return (
          <Link
            key={locale}
            href={getLocalizedHref(locale)}
            hrefLang={locale}
            className={[
              "rounded-md border px-2 py-1 transition-colors",
              isActive
                ? "border-white/20 bg-white/15 text-white"
                : "border-white/10 text-blue-50 hover:border-white/25 hover:bg-white/10",
            ].join(" ")}
            aria-current={isActive ? "page" : undefined}
          >
            {locale.toUpperCase()}
          </Link>
        );
      })}
    </nav>
  );
}
